import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faFile,
  faUser,
  faRightToBracket,
  faUserClock,
  faUserXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  fetchAuthNext,
  fetchPublicProfile,
  fetchSkpEnvironment,
  fetchSpaceInvitationDetails,
  getSpaceInvitationAvatarUrl,
} from "../../api";
import Avatar from "../../components/avatar";
import {
  AuthNextResolution,
  SkpEnvironment,
  SpaceInvitationDetails,
} from "../../entities";
import {
  DRIVE_FAVICON_HREF,
  DRIVE_PRODUCT_NAME,
  usePageChrome,
} from "../../hooks/usePageChrome";
import {
  ConditionalEnvironmentProvider,
  useSkpEnvironment,
} from "../../providers/environment";
import { formatFileSize } from "../../utils/fileFormat";
import { readUserSession } from "../../utils/session";
import { buildInvitationAuthUrl } from "../../utils/url";
import { isApiException } from "../../utils/typeGuard";
import { SpaceAvatar } from "../../components/space/spaceAvatar";
import { ApiErrorContent } from "../error";
import TransferPageSkeleton from "../skeletons/transfer";

type SpaceInvitationPageParams = {
  spaceUid: string;
  invitee: string;
};

/**
 * An auth-next step that names somewhere to go. Per `AuthNextResolution`,
 * `nextHop` is null only for `abort`, so a login or signup without one is
 * unusable rather than merely actionless.
 */
type UsableAuthNext =
  | { nextStep: "signup" | "login"; nextHop: { fqdn: string } }
  | { nextStep: "abort" };

/** Narrows an auth-next resolution; undefined when it names no host. */
function usableAuthNext(
  authNext: AuthNextResolution
): UsableAuthNext | undefined {
  if (authNext.nextStep === "abort") return { nextStep: "abort" };
  return authNext.nextHop
    ? { nextStep: authNext.nextStep, nextHop: authNext.nextHop }
    : undefined;
}

/**
 * What the page resolves to, decided in one place (the query): a session
 * matching the invitee redirects straight to the space (drive-client accepts
 * the invitation itself), a foreign session renders `wrongAddress` without any
 * invitation calls, and everything else comes from `resolveInvitation`.
 */
type InvitationResolution =
  | {
      kind: "invitation";
      details: SpaceInvitationDetails;
      inviterName: string;
      inviterEmail: string;
      authNext: UsableAuthNext;
    }
  | { kind: "redirecting" }
  | { kind: "wrongAddress"; account: string }
  | { kind: "noLongerAvailable"; loginFqdn: string }
  | { kind: "expired" }
  | { kind: "loadError"; apiErrorMessage?: string };

/** What the page renders from: the resolution plus the branding it found. */
type InvitationPageData = {
  resolution: InvitationResolution;
  environment?: SkpEnvironment;
};

/** The server's error text (message and details) from a failed call. */
function apiErrorMessageOf(reason: unknown): string | undefined {
  const data = (reason as AxiosError | undefined)?.response?.data;
  if (!data || !isApiException(data)) return undefined;
  const { message, details } = data.error;
  const detailText =
    Array.isArray(details) && details.length > 0 ? details.join(" ") : "";
  const text = [message, detailText].filter(Boolean).join("\n");
  return text || undefined;
}

/**
 * Resolves the invitation itself: the auth-next step decides the action, a 404
 * becomes `noLongerAvailable` when the address can log in (the link is probably
 * being re-used) and `expired` otherwise, and any other failure is `loadError`,
 * which never guesses an action.
 */
async function resolveInvitation(
  spaceUid: string,
  invitee: string
): Promise<InvitationResolution> {
  // The profile call needs only the details result, not auth/next.
  const detailsPromise = fetchSpaceInvitationDetails(spaceUid, invitee);
  const [detailsResult, authNextResult, inviterResult] =
    await Promise.allSettled([
      detailsPromise,
      fetchAuthNext(invitee),
      detailsPromise.then((details) => fetchPublicProfile(details.inviterUid)),
    ]);

  // Without auth/next there is no action to offer.
  if (authNextResult.status === "rejected") {
    return {
      kind: "loadError",
      apiErrorMessage: apiErrorMessageOf(authNextResult.reason),
    };
  }
  const authNext = authNextResult.value;

  if (detailsResult.status === "rejected") {
    const detailsStatus = (detailsResult.reason as AxiosError | undefined)
      ?.response?.status;
    if (detailsStatus !== 404) {
      return {
        kind: "loadError",
        apiErrorMessage: apiErrorMessageOf(detailsResult.reason),
      };
    }
    return authNext.nextStep === "login" && authNext.nextHop
      ? { kind: "noLongerAvailable", loginFqdn: authNext.nextHop.fqdn }
      : { kind: "expired" };
  }

  // The headline needs the inviter's display name.
  if (inviterResult.status === "rejected") {
    return {
      kind: "loadError",
      apiErrorMessage: apiErrorMessageOf(inviterResult.reason),
    };
  }

  // An open invitation the card cannot act on is not worth showing.
  const usable = usableAuthNext(authNext);
  if (!usable) return { kind: "loadError" };

  return {
    kind: "invitation",
    details: detailsResult.value,
    inviterName: inviterResult.value.name,
    inviterEmail: inviterResult.value.email,
    authNext: usable,
  };
}

/**
 * Public landing page for a space invitation link. It informs (who invites
 * into which space) and routes the invitee to signup or login on the host
 * auth/next names; accepting the invitation stays drive-client's job.
 */
export default function SpaceInvitationPage() {
  const { spaceUid, invitee } = useParams<SpaceInvitationPageParams>();
  if (!spaceUid || !invitee) throw new Error("invitation params are undefined");

  const { status, data } = useQuery<InvitationPageData>({
    retry: false,
    refetchOnWindowFocus: false,
    queryKey: ["spaceInvitation", spaceUid, invitee],
    queryFn: async () => {
      // A session decides first: match redirects, mismatch stops all calls.
      const sessionEmail = readUserSession()?.email;
      if (
        sessionEmail &&
        sessionEmail.toLowerCase() === invitee.toLowerCase()
      ) {
        window.location.replace(
          `${window.location.origin}/drive/spaces/${spaceUid}`
        );
        return { resolution: { kind: "redirecting" } };
      }

      // Branding for the banner + footer; best-effort, the page renders
      // without it. Carried in the data rather than a ref so a remount served
      // from the cache is branded on its first pass.
      const environmentPromise = fetchSkpEnvironment().catch(() => undefined);

      const resolution: InvitationResolution = sessionEmail
        ? { kind: "wrongAddress", account: sessionEmail }
        : await resolveInvitation(spaceUid, invitee);

      return { resolution, environment: await environmentPromise };
    },
  });

  const environment = data?.environment;
  // `error` is unreachable while every outcome comes back as a union member,
  // but a throw added to the query later must not blank the page.
  const resolution: InvitationResolution | undefined =
    status === "error" ? { kind: "loadError" } : data?.resolution;

  // Drive tab chrome; the title names the space once the invitation resolved.
  usePageChrome({
    title:
      resolution?.kind === "invitation"
        ? `${resolution.details.space.name} - ${DRIVE_PRODUCT_NAME}`
        : DRIVE_PRODUCT_NAME,
    faviconHref: DRIVE_FAVICON_HREF,
  });

  return (
    <ConditionalEnvironmentProvider
      condition={!!environment}
      environment={environment}
    >
      <TransferPageSkeleton
        childrenVerticalAlignment="center"
        hasMailboxLoaded={!!environment}
      >
        {!resolution || resolution.kind === "redirecting" ? null : (
          <InvitationResolutionView
            resolution={resolution}
            spaceUid={spaceUid}
            invitee={invitee}
          />
        )}
      </TransferPageSkeleton>
    </ConditionalEnvironmentProvider>
  );
}

/** Maps a resolution to its card / notice. */
function InvitationResolutionView({
  resolution,
  spaceUid,
  invitee,
}: {
  resolution: InvitationResolution;
  spaceUid: string;
  invitee: string;
}) {
  const { t } = useTranslation();

  switch (resolution.kind) {
    case "invitation":
      return (
        <InvitationHero
          resolution={resolution}
          spaceUid={spaceUid}
          invitee={invitee}
        />
      );
    case "expired":
      return (
        <Notice
          icon={faUserClock}
          headline={t("Invitation link expired")}
          body={t(
            "Please ask the sender to resend the invitation. You will then receive a new link by email."
          )}
          action={<HomepageButton />}
        />
      );
    case "noLongerAvailable":
      return (
        <Notice
          icon={faRightToBracket}
          headline={t("Invitation no longer available")}
          body={t(
            "Log in to open the space. If you have no access to it, please ask the sender for a new invitation."
          )}
          action={
            <ActionButton
              href={buildInvitationAuthUrl(
                resolution.loginFqdn,
                spaceUid,
                invitee
              )}
            >
              {t("Log in")}
            </ActionButton>
          }
        />
      );
    case "wrongAddress":
      return (
        <Notice
          icon={faUserXmark}
          headline={t("Invitation for a different address")}
          body={t(
            "You are currently signed in as {{account}}, but the invitation was sent to {{invitee}}. Please sign in from the homepage with {{invitee}} to accept the invitation.",
            { invitee, account: resolution.account }
          )}
          action={<HomepageButton />}
        />
      );
    case "redirecting":
      return null;
    case "loadError":
      return (
        <CenteredColumn>
          <Box sx={{ width: "100%" }}>
            <ApiErrorContent errorMessage={resolution.apiErrorMessage} />
          </Box>
        </CenteredColumn>
      );
  }
}

// ==========================================================================
//                                Invitation
// ==========================================================================

const cardBodyTextSx = {
  fontFamily: "IBM Plex Sans",
  fontWeight: 400,
  fontSize: 14,
  lineHeight: 1.35,
  color: "#1a1a1a",
};

/** One shared orbit speed; mirrored by --intro-orbit-duration in the css. */
const INTRO_ORBIT_DURATION_MS = 3950;

/**
 * The intro's file satellites, in ring order. All fly at one speed with evenly
 * spread angles, so the ring never crowds; the small delays stagger the pop-in
 * and the dive, and each start angle is advanced by what its delay costs, which
 * keeps the flying ring exactly even.
 */
const INTRO_SATELLITES = [
  { icon: "pdf.svg", delay: 0 },
  { icon: "excel.svg", delay: 140 },
  { icon: "word.svg", delay: 60 },
  { icon: "zip.svg", delay: 220 },
  { icon: "video.svg", delay: 100 },
  { icon: "unknown.svg", delay: 180 },
  { icon: "image.svg", delay: 40 },
  { icon: "powerpoint.svg", delay: 260 },
  { icon: "txt.svg", delay: 120 },
  { icon: "csv.svg", delay: 200 },
  { icon: "audio.svg", delay: 80 },
];

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/**
 * One-time intro for the hero's space disc: file icons orbit the blurhash
 * like satellites, get pulled in, and the disc absorbs them before the real
 * avatar reveals and shimmers once.
 */
function SpaceAvatarIntro({
  url,
  blurhash,
  done,
  onDone,
}: {
  url: string;
  blurhash?: string | null;
  /** Whether the intro has finished; owned by the page section around it. */
  done: boolean;
  onDone: () => void;
}) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [shimmer, setShimmer] = useState(false);

  // Safety net: never leave the page held if the animation cannot finish.
  useEffect(() => {
    if (done) return;
    const timer = window.setTimeout(onDone, 5600);
    return () => window.clearTimeout(timer);
  }, [done, onDone]);

  // The shimmer runs once, shortly after the reveal transition settled.
  useEffect(() => {
    if (!done || !imageLoaded) return;
    const timer = window.setTimeout(() => setShimmer(true), 700);
    return () => window.clearTimeout(timer);
  }, [done, imageLoaded]);

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        className={done ? undefined : "planet-absorb"}
        onAnimationEnd={(event) => {
          if (event.animationName === "planetAbsorb") onDone();
        }}
      >
        <SpaceAvatar
          url={url}
          size={140}
          iconFontSize={52}
          withRing
          blurhash={blurhash}
          holdReveal={!done}
          shimmer={shimmer}
          onImageLoaded={() => setImageLoaded(true)}
        />
      </Box>
      {!done &&
        INTRO_SATELLITES.map(({ icon, delay }, index) => (
          <div
            key={icon}
            className="sat"
            style={
              {
                "--sat-angle": `${
                  (360 / INTRO_SATELLITES.length) * index +
                  (360 * delay) / INTRO_ORBIT_DURATION_MS
                }deg`,
                "--sat-delay": `${delay}ms`,
              } as CSSProperties
            }
          >
            <div className="sat-angle">
              <div className="sat-spin">
                <div className="sat-arm">
                  <div className="sat-counter">
                    <div className="sat-counterspin">
                      <div className="sat-chip">
                        <img
                          src={`/assets/fileIcons/animation/${icon}`}
                          alt=""
                          draggable={false}
                          className="h-full w-full object-contain"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
    </Box>
  );
}

/**
 * Layout variant "b": leads with the inviter for credibility and presents the
 * space the way drive-client does, so disc and name read as one object.
 */
function InvitationHero({
  resolution,
  spaceUid,
  invitee,
}: {
  resolution: Extract<InvitationResolution, { kind: "invitation" }>;
  spaceUid: string;
  invitee: string;
}) {
  const { t } = useTranslation();
  const { inviterName, inviterEmail, details, authNext } = resolution;

  // The auth-next step decides the whole action area: the closing line and
  // the primary action.
  let closing: ReactNode;
  let action: ReactNode;
  switch (authNext.nextStep) {
    case "signup": {
      const authUrl = buildInvitationAuthUrl(
        authNext.nextHop.fqdn,
        spaceUid,
        invitee
      );
      closing = t("To participate, you need a free TeamBeam account.");
      action = (
        <ActionButton href={authUrl}>{t("Create account")}</ActionButton>
      );
      break;
    }
    case "login":
      closing = t("Log in with your TeamBeam account to join.");
      action = (
        <ActionButton
          href={buildInvitationAuthUrl(
            authNext.nextHop.fqdn,
            spaceUid,
            invitee
          )}
        >
          {t("Log in")}
        </ActionButton>
      );
      break;
    case "abort":
      closing = t(
        "Self-registration is not available on this server. Please contact an administrator or the person who invited you."
      );
      action = <HomepageButton />;
      break;
  }
  const personalNote = details.personalNote?.trim();

  // The disc plays its intro alone; the avatar then unblurs while everything
  // else waits one more beat before rising in, keeping the emphasis on the
  // disc for a moment.
  const [introDone, setIntroDone] = useState(prefersReducedMotion);
  const [contentShown, setContentShown] = useState(prefersReducedMotion);
  const finishIntro = useCallback(() => setIntroDone(true), []);
  useEffect(() => {
    if (!introDone || contentShown) return;
    const timer = window.setTimeout(() => setContentShown(true), 250);
    return () => window.clearTimeout(timer);
  }, [introDone, contentShown]);
  // `invisible` keeps the hidden content out of the tab order and hit testing
  // (opacity alone leaves it clickable); the class swap restores visibility.
  const riseClass = contentShown ? "invite-rise" : "invisible opacity-0";

  return (
    <CenteredColumn gap={0} maxWidth={480}>
      <Stack className={riseClass} alignItems="center" spacing="6px">
        <Avatar
          isAnonymous={false}
          uid={details.inviterUid}
          name={inviterName}
          size={50}
          classname="drop-shadow-none"
          outline="ring"
        />
        <Box sx={{ textAlign: "center", maxWidth: "100%" }}>
          <Typography
            component="h1"
            sx={{
              fontWeight: 400,
              fontSize: 16,
              lineHeight: "20px",
              color: "#1a1a1a",
              overflowWrap: "break-word",
            }}
          >
            {t("Invitation from {{inviterName}}", { inviterName })}
          </Typography>
          <Typography
            sx={{
              mt: 0.25,
              fontSize: 12,
              lineHeight: "15px",
              color: "#1a1a1a",
              overflowWrap: "anywhere",
            }}
          >
            {inviterEmail}
          </Typography>
        </Box>
      </Stack>

      <Box
        sx={{
          position: "relative",
          width: "100%",
          mt: "25px",
          minHeight: 290,
          borderRadius: "14px",
          border: "1px solid",
          borderColor: contentShown ? "#dadada" : "transparent",
          bgcolor: contentShown ? "#ffffff" : "transparent",
          transition: "background-color 400ms ease, border-color 400ms ease",
          px: 3,
          py: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
            width: "100%",
          }}
        >
          <SpaceAvatarIntro
            url={getSpaceInvitationAvatarUrl(spaceUid, invitee, 280)}
            blurhash={details.space.blurhash}
            done={introDone}
            onDone={finishIntro}
          />
          <Box
            className={riseClass}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
              width: "100%",
            }}
          >
            <Box sx={{ textAlign: "center", maxWidth: "100%" }}>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: 21,
                  // Wrapping text needs breathing room; the extra height is
                  // taken out of the gaps around it to keep the designed
                  // one-line spacing.
                  lineHeight: 1.2,
                  color: "#1a1a1a",
                  overflowWrap: "break-word",
                }}
              >
                {details.space.name}
              </Typography>
              <Typography
                sx={{
                  mt: 0.25,
                  fontSize: 12,
                  lineHeight: 1,
                  color: "#1a1a1a",
                }}
              >
                {t("Space in TeamBeam Drive")}
              </Typography>
              <Box sx={{ pt: "5px" }}>
                <SpaceMetaRow space={details.space} />
              </Box>
              {details.space.description && (
                <Typography
                  sx={{
                    ...cardBodyTextSx,
                    mt: 0.75,
                    overflowWrap: "break-word",
                  }}
                >
                  {details.space.description}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {personalNote && (
        <Typography
          className={riseClass}
          sx={{
            mt: "30px",
            fontSize: 15,
            lineHeight: 1.5,
            fontStyle: "italic",
            color: "#5c5c66",
            textAlign: "center",
            overflowWrap: "break-word",
            maxWidth: "100%",
          }}
        >
          {`“${personalNote}”`}
        </Typography>
      )}

      <Typography
        className={riseClass}
        sx={{
          mt: "30px",
          fontSize: 14,
          lineHeight: "18px",
          color: "#1a1a1a",
          textAlign: "center",
        }}
      >
        {closing}
      </Typography>

      <Box
        className={riseClass}
        sx={{ mt: "20px", width: "100%", maxWidth: 360, mx: "auto" }}
      >
        {action}
      </Box>
    </CenteredColumn>
  );
}

/** Participant and volume facts, pipe-separated; hidden without numbers. */
function SpaceMetaRow({ space }: { space: SpaceInvitationDetails["space"] }) {
  const { t, i18n } = useTranslation();

  const participants =
    typeof space.collaboratorCount === "number" && space.collaboratorCount > 0
      ? space.collaboratorCount === 1
        ? t("1 participant")
        : t("{{count}} participants", { count: space.collaboratorCount })
      : undefined;
  const volume =
    typeof space.usedVolume === "number" && space.usedVolume > 0
      ? formatFileSize(space.usedVolume, i18n.language)
      : undefined;
  if (!participants && !volume) return null;

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      alignItems="center"
      justifyContent="center"
      sx={{
        columnGap: 1,
        rowGap: 0.75,
        color: "#999999",
        fontSize: 12,
        lineHeight: 1,
      }}
    >
      {participants && <MetaChip icon={faUser}>{participants}</MetaChip>}
      {participants && volume && <span aria-hidden>|</span>}
      {volume && <MetaChip icon={faFile}>{volume}</MetaChip>}
    </Stack>
  );
}

/** One icon + label pair in the space meta row. */
function MetaChip({ icon, children }: { icon: IconProp; children: ReactNode }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{ fontSize: 12, lineHeight: 1 }}
    >
      <FontAwesomeIcon icon={icon} style={{ fontSize: 12 }} />
      <span>{children}</span>
    </Stack>
  );
}

// ==========================================================================
//                                 Notices
// ==========================================================================

/** A terminal state: icon disc, headline, explanation and one action. */
function Notice({
  icon,
  headline,
  body,
  action,
}: {
  icon: IconProp;
  headline: string;
  body: string;
  action: ReactNode;
}) {
  return (
    <CenteredColumn>
      <Box
        sx={{
          width: 120,
          height: 120,
          borderRadius: "50%",
          bgcolor: "color-mix(in srgb, var(--primary-color) 12%, transparent)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--primary-color)",
        }}
      >
        <FontAwesomeIcon icon={icon} style={{ fontSize: 52 }} />
      </Box>
      <Typography
        component="h1"
        sx={{
          fontWeight: 700,
          fontSize: { xs: 22, sm: 26 },
          lineHeight: 1.2,
          color: "#1a1a1a",
          textAlign: "center",
        }}
      >
        {headline}
      </Typography>
      <Typography
        sx={{
          fontSize: 15,
          lineHeight: 1.5,
          color: "#5c5c66",
          textAlign: "center",
          whiteSpace: "pre-line",
        }}
      >
        {body}
      </Typography>
      {action}
    </CenteredColumn>
  );
}

/** Leads out of a dead end, to the org's portal or the host root. */
function HomepageButton() {
  const { t } = useTranslation();
  const environment = useSkpEnvironment();
  const href =
    environment?.portalUrl?.toString() ?? `https://${window.location.hostname}`;

  return <ActionButton href={href}>{t("Go to homepage")}</ActionButton>;
}

const actionButtonSx = {
  fontFamily: "IBM Plex Sans",
  fontWeight: 500,
  fontSize: 16,
  // Rendered as an <a> when given href; pin the label color against the
  // global anchor styles, which otherwise tint it on hover/visited.
  "&, &:hover, &:visited": { color: "#ffffff" },
  transition: "transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
  "&:active": { color: "#ffffff", transform: "scale(0.97)" },
};

/** The page's primary action, a full-width link button. */
function ActionButton({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) {
  return (
    <Button
      variant="contained"
      size="large"
      fullWidth
      href={href}
      sx={actionButtonSx}
    >
      {children}
    </Button>
  );
}

// ==========================================================================
//                                 Layout
// ==========================================================================

/** The page's single narrow, centered column shared by card and notices. */
function CenteredColumn({
  children,
  gap = 3,
  maxWidth = 440,
}: {
  children: ReactNode;
  gap?: number;
  maxWidth?: number;
}) {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth,
        mx: "auto",
        px: 2.5,
        py: 6,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap,
      }}
    >
      {children}
    </Box>
  );
}
